import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, ArrowRightOnRectangleIcon, NoSymbolIcon, ClockIcon } from '@heroicons/react/24/outline';

// historyId/isPortal come from the backend's normalized exit-record shape —
// always the correct id for the Staff History endpoint (users.id for portal
// staff, non_portal_staff.id otherwise). Never derive this from `_id`, whose
// numeric part is a different table's own row id for teacher/librarian/
// hostel_member and would silently link to the wrong person's history.
const historyLink = (r) => r.isPortal
  ? `/staff-history/${r.historyId}?role=${r.role}&isPortal=true`
  : `/staff-history/${r.historyId}?isPortal=false`;

const ini     = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) || 'S';
const fmtDate = (d)    => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const ROLE_LABELS = {
  teacher: 'Teacher', finance: 'Finance', coordinator: 'Coordinator',
  librarian: 'Librarian', hostel_member: 'Hostel Member', non_portal_staff: 'Non-Portal Staff',
};

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .te*{box-sizing:border-box;} .te{font-family:'Sora',sans-serif;color:#1a1d2e;animation:teFade .4s ease both;}
  @keyframes teFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .te-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .te-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .te-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .filter-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:.8rem;}
  .filter-tab{padding:7px 18px;border-radius:99px;border:1.5px solid #e0e4f0;background:white;font-family:'Sora',sans-serif;font-size:.76rem;font-weight:600;cursor:pointer;color:#6b7194;transition:all .15s;}
  .filter-tab.active{background:#1a1d2e;color:white;border-color:#1a1d2e;}
  .filter-tab.resigned.active{background:#d97706;border-color:#d97706;}
  .filter-tab.terminated.active{background:#ef4444;border-color:#ef4444;}
  .role-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:1.2rem;}
  .role-tab{padding:5px 14px;border-radius:8px;border:1.5px solid #e0e4f0;background:#f8f9fc;font-family:'Sora',sans-serif;font-size:.72rem;font-weight:600;cursor:pointer;color:#9094a8;transition:all .15s;}
  .role-tab.active{background:#eef2ff;color:#3b6ef8;border-color:#3b6ef8;}
  .te-toolbar{display:flex;gap:.7rem;flex-wrap:wrap;align-items:center;margin-bottom:1.2rem;}
  .te-search-wrap{position:relative;flex:1;max-width:280px;}
  .te-search-wrap svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:#9094a8;pointer-events:none;}
  .te-search{font-family:'Sora',sans-serif;font-size:.8rem;width:100%;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:10px;background:white;outline:none;}
  .te-search:focus{border-color:#1a1d2e;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .panel-hdr{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid #f0f2f8;}
  .panel-icon{width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .panel-icon svg{width:16px;height:16px;stroke-width:1.8;}
  .panel-icon.resigned{background:#fffbeb;color:#d97706;}
  .panel-icon.terminated{background:#fef2f2;color:#ef4444;}
  .panel-icon.all{background:#f0f2f7;color:#6b7194;}
  .tbl{width:100%;border-collapse:collapse;font-size:.8rem;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:10px 16px;text-align:center !important;font-size:.65rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{ padding:11px 16px;color:#3a3f58;vertical-align:middle; text-align:center !important; }
  .name-cell{display:flex;align-items:center;gap:10px;}
  .avatar{width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .avatar.resigned{background:linear-gradient(135deg,#d97706,#f59e0b);}
  .avatar.terminated{background:linear-gradient(135deg,#ef4444,#dc2626);}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ne{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.75rem;color:#6b7194;}
  .exit-pill{display:inline-flex;align-items:center;gap:4px;font-size:.67rem;font-weight:600;padding:3px 9px;border-radius:99px;}
  .exit-pill svg{width:11px;height:11px;stroke-width:2;}
  .exit-pill.resigned{background:#fffbeb;color:#d97706;}
  .exit-pill.terminated{background:#fef2f2;color:#ef4444;}
  .tag{display:inline-block;padding:2px 8px;border-radius:99px;font-size:.67rem;font-weight:600;background:#f0f2f7;color:#6b7194;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:4px;transition:.13s;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.history{color:#8b5cf6;} .act-btn.history:hover{background:#f3f0ff;border-color:#8b5cf6;}
  .empty{text-align:center;padding:52px 20px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#6b7194;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg);}}
  .cards{display:none;padding:10px;}
  .card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .card-row{display:flex;justify-content:space-between;align-items:flex-start;font-size:.75rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .card-row:last-of-type{border-bottom:none;}
  .card-lbl{color:#9094a8;font-weight:600;white-space:nowrap;flex-shrink:0;}
  @media(max-width:768px){.te-title{font-size:1.2rem;}.tbl-wrap{display:none!important;}.cards{display:block;}.te-search-wrap{max-width:100%;}}
`;

const TYPE_TABS = [
  { key:'',           label:'All' },
  { key:'resigned',   label:'Resigned' },
  { key:'terminated', label:'Terminated' },
];

const ROLE_TABS = [
  { key:'',                label:'All Roles' },
  { key:'coordinator',      label:'Coordinator' },
  { key:'finance',          label:'Finance' },
  { key:'hostel_member',    label:'Hostel Member' },
  { key:'librarian',        label:'Librarian' },
  { key:'non_portal_staff', label:'Non-Portal Staff' },
  { key:'teacher',          label:'Teacher' },
];

export default function StaffExits() {
  const navigate = useNavigate();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('');
  const [role,    setRole]    = useState('');
  const [search,  setSearch]  = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/staff-exits');
      setRows(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load'); }
    finally   { setLoading(false); }
  };

  const filtered = rows.filter(r => {
    if (filter && r.exitType !== filter) return false;
    if (role && r.role !== role) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.name?.toLowerCase().includes(q) && !r.employeeId?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = {
    '':           rows.length,
    resigned:     rows.filter(r => r.exitType === 'resigned').length,
    terminated:   rows.filter(r => r.exitType === 'terminated').length,
  };

  return (
    <div className="te"><style>{style}</style>
      <div className="te-hdr">
        <div>
          <h1 className="te-title">Staff Exits</h1>
          <p className="te-sub">Resigned and terminated staff across every role. Historical records are preserved.</p>
        </div>
      </div>

      <div className="filter-tabs">
        {TYPE_TABS.map(t => (
          <button key={t.key}
            className={`filter-tab ${t.key} ${filter===t.key?'active':''}`}
            onClick={() => setFilter(t.key)}>
            {t.label} ({counts[t.key]})
          </button>
        ))}
      </div>

      <div className="role-tabs">
        {ROLE_TABS.map(t => (
          <button key={t.key}
            className={`role-tab ${role===t.key?'active':''}`}
            onClick={() => setRole(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="te-toolbar">
        <div className="te-search-wrap">
          <MagnifyingGlassIcon />
          <input className="te-search" placeholder="Search by name or employee ID…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="panel">
        <div className="panel-hdr">
          <div className={`panel-icon ${filter || 'all'}`}>
            {filter === 'resigned'   ? <ArrowRightOnRectangleIcon /> :
             filter === 'terminated' ? <NoSymbolIcon /> :
             <ArrowRightOnRectangleIcon />}
          </div>
          <div>
            <div style={{fontWeight:600,fontSize:'.88rem'}}>
              {filter === 'resigned' ? 'Resigned Staff' : filter === 'terminated' ? 'Terminated Staff' : 'All Exits'}
            </div>
            <div style={{fontSize:'.72rem',color:'#9094a8'}}>{loading ? 'Loading…' : `${filtered.length} record${filtered.length !== 1 ? 's' : ''}`}</div>
          </div>
        </div>

        {loading ? (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px 0',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">No {filter || 'exited'} staff found.</div>
        ) : (
          <>
            <div className="tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr>
                  <th>Staff Member</th><th>Role</th><th>Employee ID</th><th>Exit Type</th>
                  <th>Exit Date</th><th>Joining Date</th><th>Note</th><th>Marked By</th><th>History</th>
                </tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r._id}>
                      <td>
                        <div className="name-cell">
                          <div className={`avatar ${r.exitType}`}>{ini(r.name)}</div>
                          <div><div className="nm">{r.name}</div><div className="ne">{r.email}</div></div>
                        </div>
                      </td>
                      <td><span className="tag">{ROLE_LABELS[r.role] || r.role}</span></td>
                      <td><span className="mono">{r.employeeId || '—'}</span></td>
                      <td>
                        <span className={`exit-pill ${r.exitType}`}>
                          {r.exitType === 'resigned' ? <ArrowRightOnRectangleIcon /> : <NoSymbolIcon />}
                          {r.exitType === 'resigned' ? 'Resigned' : 'Terminated'}
                        </span>
                      </td>
                      <td><span className="mono">{fmtDate(r.exitDate)}</span></td>
                      <td><span className="mono">{fmtDate(r.joiningDate)}</span></td>
                      <td><span style={{fontSize:'.76rem',color:'#6b7194'}}>{r.exitNote || '—'}</span></td>
                      <td><span style={{fontSize:'.76rem',color:'#9094a8'}}>{r.exitBy?.name || '—'}</span></td>
                      <td><button className="act-btn history" title="Full History" onClick={()=>navigate(historyLink(r))}><ClockIcon/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cards">
              {filtered.map(r => (
                <div key={r._id} className="card">
                  <div className="card-top">
                    <div className="name-cell">
                      <div className={`avatar ${r.exitType}`}>{ini(r.name)}</div>
                      <div><div className="nm">{r.name}</div><div className="ne">{r.email}</div></div>
                    </div>
                    <span className={`exit-pill ${r.exitType}`}>
                      {r.exitType === 'resigned' ? <ArrowRightOnRectangleIcon /> : <NoSymbolIcon />}
                      {r.exitType === 'resigned' ? 'Resigned' : 'Terminated'}
                    </span>
                  </div>
                  <div className="card-row"><span className="card-lbl">Role</span><span className="tag">{ROLE_LABELS[r.role] || r.role}</span></div>
                  <div className="card-row"><span className="card-lbl">Employee ID</span><span className="mono">{r.employeeId || '—'}</span></div>
                  <div className="card-row"><span className="card-lbl">Exit Date</span><span className="mono">{fmtDate(r.exitDate)}</span></div>
                  <div className="card-row"><span className="card-lbl">Joining Date</span><span className="mono">{fmtDate(r.joiningDate)}</span></div>
                  {r.exitNote && <div className="card-row"><span className="card-lbl">Note</span><span style={{fontSize:'.74rem',color:'#6b7194',textAlign:'right'}}>{r.exitNote}</span></div>}
                  {r.exitBy?.name && <div className="card-row"><span className="card-lbl">Marked By</span><span style={{fontSize:'.74rem',color:'#9094a8'}}>{r.exitBy.name}</span></div>}
                  <button className="act-btn history" style={{marginTop:8,width:'100%',justifyContent:'center',gap:6,fontFamily:"'Sora',sans-serif",fontSize:'.74rem',fontWeight:600}} onClick={()=>navigate(historyLink(r))}><ClockIcon/>Full History</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
